import {
  PLATFORM_LABEL,
  getAgentId,
  getAgentName,
  getConnectorId,
} from "@p0security/shared/types/ai-inventory";
import type { Edge, Node } from "@xyflow/react";
import { color } from "theme/aiInventory";

import { PLATFORM_META } from "../platforms/meta.ts";
import { serverNodeId } from "./matchServer.ts";
import type { AgentGraphData } from "./types.ts";

// ── edge styles ───────────────────────────────────────────────────────────────

const ENROLLED_EDGE_STYLE = { stroke: color.integration, strokeWidth: 1.5 };
const DIRECT_EDGE_STYLE = {
  stroke: color.unmanaged,
  strokeWidth: 1.5,
  strokeDasharray: "5 4",
};
const NEUTRAL_EDGE_STYLE = { stroke: "#9ca3af", strokeWidth: 1 };

// ── helpers ───────────────────────────────────────────────────────────────────

const makeEdge = (
  source: string,
  target: string,
  style:
    | typeof DIRECT_EDGE_STYLE
    | typeof ENROLLED_EDGE_STYLE
    | typeof NEUTRAL_EDGE_STYLE,
  layoutHints?: { minlen?: number }
): Edge => ({
  id: `e-${source}-${target}`,
  source,
  target,
  style,
  ...(layoutHints ? { data: layoutHints } : {}),
});

// ── buildGraph ────────────────────────────────────────────────────────────────

/**
 * Graph topology:
 *   Platform → Agent → P0 Gateway → [ MCP Server ] → [ Risk ]
 *                    ↳ [ Unmanaged Connector ] → [ Risk? ]
 */
export const buildGraph = (
  data: AgentGraphData,
  _expandedConnectorIds: Set<string>
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const agentId = getAgentId(data.agent);
  const agentLabel = getAgentName(data.agent);
  const agentNodeId = `agent-${agentId}`;

  const isLocal =
    PLATFORM_META[data.agent.platform].supportsEnvironments === false;

  // Platform node (skipped for local runtimes)
  if (!isLocal) {
    nodes.push({
      id: "platform",
      type: "platform",
      data: {
        label: PLATFORM_LABEL[data.agent.platform],
        platform: data.agent.platform,
      },
      position: { x: 0, y: 0 },
    });
  }

  // Agent node
  nodes.push({
    id: agentNodeId,
    type: "agent",
    data: { label: agentLabel, agentId, platform: data.agent.platform },
    position: { x: 0, y: 0 },
  });

  if (!isLocal) {
    edges.push(makeEdge("platform", agentNodeId, NEUTRAL_EDGE_STYLE));
  }

  const hasGateway = data.p0Mcp !== null;

  // P0 Gateway node (control plane shield)
  if (hasGateway) {
    nodes.push({
      id: "p0mcp",
      type: "p0mcp",
      data: { context: data.p0Mcp, agentId },
      position: { x: 0, y: 0 },
    });
    edges.push(makeEdge(agentNodeId, "p0mcp", ENROLLED_EDGE_STYLE));
  }

  // Render every registered MCP server as a live node behind the gateway.
  // Labels and ids come straight from Firestore (`integrations/mcp`).
  if (hasGateway) {
    for (const server of data.mcpServers) {
      const sid = serverNodeId(server);
      nodes.push({
        id: sid,
        type: "mcpServer",
        data: {
          server,
          connector: undefined,
          toolCount: 0, // SidecarGraph overlays live counts from access logs
          live: true,
          expanded: false,
        },
        position: { x: 0, y: 0 },
      });
      edges.push(makeEdge("p0mcp", sid, ENROLLED_EDGE_STYLE));

      // Attach this server's risks as leaf nodes.
      const risks = data.serverRisks[server.id] ?? [];
      for (const risk of risks) {
        const riskNodeId = `risk-${risk.id}`;
        nodes.push({
          id: riskNodeId,
          type: "risk",
          data: { risk },
          position: { x: 0, y: 0 },
        });
        edges.push(makeEdge(sid, riskNodeId, NEUTRAL_EDGE_STYLE));
      }
    }
  }

  // Unmanaged connectors render as their own nodes with dashed direct edges
  // from the agent — they bypass the gateway so we have no tool-call
  // visibility into them.
  for (const c of data.connectors) {
    if (c.managed) continue;
    const connectorId = getConnectorId(c.connector);
    const connectorNodeId = `connector-${connectorId}`;
    nodes.push({
      id: connectorNodeId,
      type: "connector",
      data: {
        connector: c.connector,
        managed: c.managed,
        toolCount: c.tools.length,
        expanded: false,
      },
      position: { x: 0, y: 0 },
    });
    edges.push(
      makeEdge(agentNodeId, connectorNodeId, DIRECT_EDGE_STYLE, {
        minlen: hasGateway ? 2 : 1,
      })
    );

    // Attach this connector's risks (e.g. missing audit trail) as leaf nodes,
    // mirroring the managed-server risk pattern above.
    for (const risk of c.risks) {
      const riskNodeId = `risk-${risk.id}`;
      nodes.push({
        id: riskNodeId,
        type: "risk",
        data: { risk },
        position: { x: 0, y: 0 },
      });
      edges.push(makeEdge(connectorNodeId, riskNodeId, NEUTRAL_EDGE_STYLE));
    }
  }

  return { nodes, edges };
};
